// src/admin/components/TicketPicker.jsx
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import StatusPill from '../../shared/components/pill/StatusPill';
import FormInput from '../../shared/components/FormInput';
import useTicketSearch from '../hooks/useTicketSearch';

/**
 * Picks an issue from the organization's upstream ticketing system.
 *
 * Degrades to a plain free-text field when no ticketing integration is configured, so
 * organizations without one see exactly the field they had before — and likewise when the
 * integration exists but cannot be reached, where the difference is that the fallback is
 * announced rather than silent.
 */
export default function TicketPicker({ selected, reference, onSelect, onReferenceChange }) {
  const {
    query, setQuery, tickets, loading, error, available, unreachableProjects,
  } = useTicketSearch();
  const [open, setOpen] = useState(false);

  // A lookup that fails mid-word would otherwise take the half-typed key with it when the
  // field swaps from search to free text.
  useEffect(() => {
    if (error && query && !reference) onReferenceChange(query);
  }, [error, query, reference, onReferenceChange]);

  // Before the fallbacks: a ticket chosen earlier stays chosen. The hook keeps searching in
  // the background, and an outage that starts afterwards must not drop the admin's choice.
  if (selected) {
    return (
      <div className="mb-3">
        <label className="form-label" htmlFor="ticket_selected">Ticket</label>
        <div className="d-flex align-items-start justify-content-between border rounded p-2" id="ticket_selected">
          <div className="me-2">
            <div className="fw-semibold">
              {selected.key}
              {selected.status && (
                <StatusPill tone="neutral" size="inline" className="ms-2">{selected.status}</StatusPill>
              )}
            </div>
            {selected.title && <div className="text-muted small">{selected.title}</div>}
          </div>
          <button
            type="button"
            className="btn btn-sm btn-link text-decoration-none"
            onClick={() => { onSelect(null); setQuery(''); setOpen(false); }}
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  /**
   * No integration at all, or one that is not answering. Both end in the same free-text
   * field, because a reference the admin can type is worth more than a search box that
   * cannot answer — but an outage says so, and says what is lost by it.
   *
   * The lookup failure is deliberately not passed to FormInput as `error`. That applies
   * Bootstrap's `is-invalid`, which states that what is in the field is wrong; here the
   * field is optional, empty, untouched, and the thing that is wrong is a third-party
   * system. It is a warning about the surroundings, not a verdict on the input.
   */
  if (!available || error) {
    return (
      <div>
        {error && (
          <div className="alert alert-warning py-2 small" role="status">
            {error} You can still enter a reference below — it will be saved as text and not
            linked to the ticket.
          </div>
        )}
        <FormInput
          id="ticket_reference"
          label="Ticket Reference"
          placeholder="Enter ticket reference"
          value={reference}
          onChange={e => onReferenceChange(e.target.value)}
        />
      </div>
    );
  }

  return (
    <div className="mb-3 position-relative">
      <FormInput
        id="ticket_search"
        label={(
          <>
            Ticket
            <span className="text-muted ms-1" style={{ fontSize: '0.75rem' }}>(optional)</span>
          </>
        )}
        placeholder="Search by title or paste a ticket key"
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        autoComplete="off"
      />

      {open && (
        <ul
          className="list-group position-absolute w-100 shadow-sm"
          style={{ zIndex: 1056, maxHeight: '15rem', overflowY: 'auto' }}
        >
          {loading && <li className="list-group-item text-muted small">Searching…</li>}
          {!loading && tickets.length === 0 && unreachableProjects.length === 0 && (
            <li className="list-group-item text-muted small">No matching tickets</li>
          )}
          {!loading && tickets.length === 0 && unreachableProjects.length > 0 && (
            <li className="list-group-item small text-warning-emphasis bg-warning-subtle">
              {`Cannot reach ${unreachableProjects.join(', ')} in the ticketing system — results may be incomplete. Check the integration settings.`}
            </li>
          )}
          {!loading && tickets.map(ticket => (
            <li key={ticket.external_id} className="list-group-item list-group-item-action p-0">
              <button
                type="button"
                className="btn btn-link text-decoration-none text-start w-100 p-2 text-body"
                onClick={() => { onSelect(ticket); setOpen(false); }}
              >
                <span className="fw-semibold">{ticket.key}</span>
                {ticket.status && (
                  <StatusPill tone="neutral" size="inline" className="ms-2">{ticket.status}</StatusPill>
                )}
                <span className="d-block text-muted small">{ticket.title}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

TicketPicker.propTypes = {
  selected: PropTypes.shape({
    external_id: PropTypes.string,
    key: PropTypes.string,
    title: PropTypes.string,
    status: PropTypes.string,
  }),
  reference: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onReferenceChange: PropTypes.func.isRequired,
};

TicketPicker.defaultProps = {
  selected: null,
  reference: '',
};
